import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Claves de parámetros del sistema
 */
export const PARAMETER_KEYS = {
    /** Versión del endpoint Excel para liquidaciones agencia: 'v1' | 'v2' */
    LA_VERSION_EXCEL: 'LA_VERSION_EXCEL',
    /** Modo de prueba para envío de emails en instructivo de embarque: 'SI' | 'NO' */
    MODO_TEST_EMAIL: 'MODO_TEST_EMAIL',
    /** Email destinatario cuando MODO_TEST_EMAIL está activo */
    EMAIL_TEST: 'EMAIL_TEST',
    /** Lista de distribución interna OPB para CC en envíos de Instructivo de Embarque */
    EMAIL_LISTA_OPB: 'EMAIL_LISTA_OPB',
} as const;

export interface Parameter {
    id: string;
    key: string;
    value: string;
    description?: string;
    isActive: boolean;
    createdAt: string;
    updatedAt?: string;
}

export interface CreateParameterRequest {
    key: string;
    value: string;
    description?: string;
    isActive?: boolean;
}

export interface UpdateParameterRequest {
    value?: string;
    description?: string;
    isActive?: boolean;
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
    timestamp: string;
}

@Injectable({
    providedIn: 'root',
})
export class ParameterService {
    private apiUrl = `${environment.apiUrl}/v1`;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private http: HttpClient
    ) {}

    /**
     * Obtener todos los parámetros
     */
    getParameters(): Observable<ApiResponse<Parameter[]>> {
        const url = `${this.apiUrl}/parameters`;
        return this.http.get<ApiResponse<Parameter[]>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener parámetro por ID
     */
    getParameterById(id: string): Observable<ApiResponse<Parameter>> {
        const url = `${this.apiUrl}/parameters/${id}`;
        return this.http.get<ApiResponse<Parameter>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener parámetro por clave
     */
    getParameterByKey(key: string): Observable<ApiResponse<Parameter>> {
        const url = `${this.apiUrl}/parameters/key/${key}`;
        return this.http.get<ApiResponse<Parameter>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Crear nuevo parámetro
     */
    createParameter(
        parameterData: CreateParameterRequest
    ): Observable<ApiResponse<Parameter>> {
        const url = `${this.apiUrl}/parameters`;
        return this.http.post<ApiResponse<Parameter>>(url, parameterData, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Actualizar parámetro
     */
    updateParameter(
        id: string,
        parameterData: UpdateParameterRequest
    ): Observable<ApiResponse<Parameter>> {
        const url = `${this.apiUrl}/parameters/${id}`;
        return this.http.put<ApiResponse<Parameter>>(url, parameterData, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Eliminar parámetro
     */
    deleteParameter(id: string): Observable<ApiResponse<void>> {
        const url = `${this.apiUrl}/parameters/${id}`;
        return this.http.delete<ApiResponse<void>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener el valor de un parámetro por su clave, con valor por defecto si no existe
     * @param key Clave del parámetro (usar PARAMETER_KEYS)
     * @param defaultValue Valor por defecto si el parámetro no existe o está inactivo
     * @returns Observable con el valor del parámetro o el valor por defecto
     */
    getParameterValue(key: string, defaultValue: string): Observable<string> {
        return this.getParameterByKey(key).pipe(
            map((response) => {
                if (
                    response.success &&
                    response.data &&
                    response.data.isActive
                ) {
                    return response.data.value;
                }
                return defaultValue;
            }),
            catchError(() => {
                // Si hay error (parámetro no existe, error de red, etc.), usar valor por defecto
                return of(defaultValue);
            })
        );
    }

    /**
     * Obtener la versión del Excel para liquidaciones agencia
     * @returns Observable con 'v1' o 'v2'
     */
    getLiquidacionAgenciaExcelVersion(): Observable<'v1' | 'v2'> {
        return this.getParameterValue(
            PARAMETER_KEYS.LA_VERSION_EXCEL,
            'v1'
        ).pipe(map((value) => (value === 'v2' ? 'v2' : 'v1')));
    }

    /**
     * Obtener token de autenticación del localStorage
     */
    private getAuthToken(): string | null {
        return localStorage.getItem('access_token');
    }

    /**
     * Crear headers con token de autenticación
     */
    private getAuthHeaders(): HttpHeaders {
        const token = this.getAuthToken();
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        });
    }
}
